<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <div class="form-section group">
      <span class="icon"><icon-colored-weather /></span>
      {{ capitalize($t('messages.weatherTargeting')) }}
      <div
        v-if="canBeRemoved"
        class="actions">
        <button
          class="btn btn-danger btn-sm"
          :disabled="isReadOnly"
          @click="clear">
          <icon-delete />
          {{ capitalize($t('actions.remove')) }}
        </button>
      </div>
    </div>
    <transition name="fade">
      <div
        v-if="isMounted"
        class="form-section-content">
        <div class="form-group row">
          <div
            id="edit_creative_weather"
            class="col-sm-12">
            <weather-targeting-editor
              :wrapped-in-pane="false"
              :is-read-only="isReadOnly"
              :entity="creative"
              :is-creative-weather-targeting="true"
              :targeting-criteria="targetingCriteria"
              :selected-weather-conditions="weatherTargetingConditions"
              :temperature-code="weatherTargetingTemperatureCode"
              :temperature-value="weatherTargetingTemperatureValue"
              :selected-uv-conditions="uvTargetingConditions"
              :enable-u-v-targeting="true"
              :selected-destination-weather-conditions="destinationWeatherTargetingConditions"
              :destination-latitude="destinationLatitude"
              :destination-longitude="destinationLongitude"
              :destination-temperature-code="destinationWeatherTargetingTemperatureCode"
              :destination-temperature-value="destinationWeatherTargetingTemperatureValue"
              :destination-snow-accumulation-code="destinationSnowAccumulationCode"
              :destination-snow-accumulation-value="destinationSnowAccumulationValue"
              :destination-rain-accumulation-code="destinationRainAccumulationCode"
              :destination-rain-accumulation-value="destinationRainAccumulationValue"
              @selected-weather-conditions-changed="(x) => (weatherTargetingConditions = x)"
              @temperature-code-changed="(x) => (weatherTargetingTemperatureCode = x)"
              @temperature-value-changed="(x) => (weatherTargetingTemperatureValue = x)"
              @selected-u-v-conditions-changed="(x) => (uvTargetingConditions = x)"
              @selected-destination-weather-conditions-changed="(x) => (destinationWeatherTargetingConditions = x)"
              @destination-latitude-changed="(x) => (destinationLatitude = x)"
              @destination-longitude-changed="(x) => (destinationLongitude = x)"
              @destination-temperature-code-changed="(x) => (destinationWeatherTargetingTemperatureCode = x)"
              @destination-temperature-value-changed="(x) => (destinationWeatherTargetingTemperatureValue = x)"
              @destination-snow-accumulation-code-changed="(x) => (destinationSnowAccumulationCode = x)"
              @destination-snow-accumulation-value-changed="(x) => (destinationSnowAccumulationValue = x)"
              @destination-rain-accumulation-code-changed="(x) => (destinationRainAccumulationCode = x)"
              @destination-rain-accumulation-value-changed="(x) => (destinationRainAccumulationValue = x)"
              @cleared-local-targeting="clearLocalTargeting"
              @cleared-destination-targeting="clearDestinationTargeting" />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script type="text/babel">
  import { capitalize } from 'vue';
  import { mapActions, mapState } from 'vuex';

  import WeatherTargetingEditor from '@/common/components/controls/targeting/weather/WeatherTargetingEditor';
  import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
  import IconColoredWeather from '@/common/icons/colored/Weather';
  import IconDelete from '@/common/icons/monochrome/Delete';
  import CreativeTargetingCriterion from '@/common/models/creative/CreativeTargetingCriterion';
  import { useTargetingCriteria } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';

  export default {
    components: {
      WeatherTargetingEditor,
      IconColoredWeather,
      IconDelete,
    },
    props: {
      creative: {
        type: Object,
      },
      canBeRemoved: {
        type: Boolean,
        default: false,
      },
      isReadOnly: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isWeatherExpanded: true,
        internalDestinationLongitude: null,
        internalDestinationLatitude: null,
        isMounted: false,
        targetingCriteriaQuery: useTargetingCriteria(),
      };
    },
    computed: {
      ...mapState('orgApp', {}),
      targetingCriteria() {
        return this.targetingCriteriaQuery.items;
      },
      weatherTargetingConditions: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const weatherCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.Weather);
            if (weatherCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === weatherCriterion.id,
              );
              if (criterion != null) {
                return criterion.values.find((x) => x.code !== 'LOCATION').value.split(',');
              }
            }
          }
          return null;
        },
        set(val) {
          const weatherTargetingCriteria = this.getWeatherTargetingCriteria(
            val,
            this.weatherTargetingTemperatureCode,
            this.weatherTargetingTemperatureValue,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) => x.type !== TargetingCriteriaType.Weather && x.type !== TargetingCriteriaType.Temperature,
          );
          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(weatherTargetingCriteria == null ? [] : weatherTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      weatherTargetingTemperatureCode: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const temperatureCriterion = this.targetingCriteria.find(
              (x) => x.type === TargetingCriteriaType.Temperature,
            );
            if (temperatureCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === temperatureCriterion.id,
              );
              if (criterion != null) {
                return criterion.values[0].code;
              }
            }
          }
          return null;
        },
        set(val) {
          const weatherTargetingCriteria = this.getWeatherTargetingCriteria(
            this.weatherTargetingConditions,
            val,
            val == null ? null : this.weatherTargetingTemperatureValue,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) => x.type !== TargetingCriteriaType.Weather && x.type !== TargetingCriteriaType.Temperature,
          );

          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(weatherTargetingCriteria == null ? [] : weatherTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      weatherTargetingTemperatureValue: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const temperatureCriterion = this.targetingCriteria.find(
              (x) => x.type === TargetingCriteriaType.Temperature,
            );
            if (temperatureCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === temperatureCriterion.id,
              );
              if (criterion != null) {
                return parseFloat(criterion.values.find((x) => x.code !== 'LOCATION').value);
              }
            }
          }
          return null;
        },
        set(val) {
          const weatherTargetingCriteria = this.getWeatherTargetingCriteria(
            this.weatherTargetingConditions,
            val == null ? null : this.weatherTargetingTemperatureCode,
            val,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) => x.type !== TargetingCriteriaType.Weather && x.type !== TargetingCriteriaType.Temperature,
          );

          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(weatherTargetingCriteria == null ? [] : weatherTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      uvTargetingConditions: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const uvCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.UV);
            if (uvCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === uvCriterion.id,
              );
              if (criterion != null) {
                return criterion.values.find((x) => x.code !== 'LOCATION').value.split(',');
              }
            }
          }
          return null;
        },
        set(val) {
          const uvTargetingCriteria = this.getUVTargetingCriteria(val);
          const otherTargetingCriterion = this.targetingCriteria.filter((x) => x.type !== TargetingCriteriaType.UV);
          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(uvTargetingCriteria == null ? [] : uvTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      destinationLatitude: {
        get() {
          if (this.internalDestinationLatitude == null) {
            if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
              // The destination latitude is stored in every instance of creativeTargetingCriteria so we just needs to find one.
              const locationBasedCriteria = this.targetingCriteria.filter(
                (x) =>
                  x.type === TargetingCriteriaType.DestinationWeather ||
                  x.type === TargetingCriteriaType.DestinationTemperature ||
                  x.type === TargetingCriteriaType.DestinationSnow ||
                  x.type === TargetingCriteriaType.DestinationRain,
              );
              if (locationBasedCriteria.length > 0 && this.creative.creativeTargetingCriteria != null) {
                const criteriaIDs = locationBasedCriteria.map((x) => x.id);
                const locationBasedCriterionWithLocation = this.creative.creativeTargetingCriteria.find(
                  (x) =>
                    criteriaIDs.includes(x.targetingCriterionID) &&
                    x.values.some((v) => v.code === 'LOCATION' && v.value != null),
                );
                if (locationBasedCriterionWithLocation == null) {
                  return null;
                }
                const locationCriterionValue = locationBasedCriterionWithLocation.values.find(
                  (v) => v.code === 'LOCATION',
                );
                return Number.parseFloat(locationCriterionValue.value.split(',')[0]);
              }
            }
          }
          return this.internalDestinationLatitude;
        },
        set(val) {
          this.internalDestinationLatitude = val;
          // Update all destination criteria with the new Lat/Long

          this.destinationWeatherTargetingConditions = this.destinationWeatherTargetingConditions;
          this.destinationWeatherTargetingTemperatureCode = this.destinationWeatherTargetingTemperatureCode;
          this.destinationWeatherTargetingTemperatureValue = this.destinationWeatherTargetingTemperatureValue;
          this.destinationSnowAccumulationCode = this.destinationSnowAccumulationCode;
          this.destinationSnowAccumulationValue = this.destinationSnowAccumulationValue;
          this.destinationRainAccumulationCode = this.destinationRainAccumulationCode;
          this.destinationRainAccumulationValue = this.destinationRainAccumulationValue;
        },
      },
      destinationLongitude: {
        get() {
          if (this.internalDestinationLongitude == null) {
            if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
              // The destination longitude is stored in every instance of creativeTargetingCriteria so we just needs to find one.
              const locationBasedCriteria = this.targetingCriteria.filter(
                (x) =>
                  x.type === TargetingCriteriaType.DestinationWeather ||
                  x.type === TargetingCriteriaType.DestinationTemperature ||
                  x.type === TargetingCriteriaType.DestinationSnow ||
                  x.type === TargetingCriteriaType.DestinationRain,
              );
              if (locationBasedCriteria.length > 0 && this.creative.creativeTargetingCriteria != null) {
                const criteriaIDs = locationBasedCriteria.map((x) => x.id);
                const locationBasedCriterionWithLocation = this.creative.creativeTargetingCriteria.find(
                  (x) =>
                    criteriaIDs.includes(x.targetingCriterionID) &&
                    x.values.some((v) => v.code === 'LOCATION' && v.value != null),
                );
                if (locationBasedCriterionWithLocation == null) {
                  return null;
                }
                const locationCriterionValue = locationBasedCriterionWithLocation.values.find(
                  (v) => v.code === 'LOCATION',
                );
                return Number.parseFloat(locationCriterionValue.value.split(',')[1]);
              }
            }
          }
          return this.internalDestinationLongitude;
        },
        set(val) {
          this.internalDestinationLongitude = val;
          // Update all destination criteria with the new Lat/Long

          this.destinationWeatherTargetingConditions = this.destinationWeatherTargetingConditions;
          this.destinationWeatherTargetingTemperatureCode = this.destinationWeatherTargetingTemperatureCode;
          this.destinationWeatherTargetingTemperatureValue = this.destinationWeatherTargetingTemperatureValue;
          this.destinationSnowAccumulationCode = this.destinationSnowAccumulationCode;
          this.destinationSnowAccumulationValue = this.destinationSnowAccumulationValue;
          this.destinationRainAccumulationCode = this.destinationRainAccumulationCode;
          this.destinationRainAccumulationValue = this.destinationRainAccumulationValue;
        },
      },
      destinationWeatherTargetingConditions: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const destinationWeatherCriterion = this.targetingCriteria.find(
              (x) => x.type === TargetingCriteriaType.DestinationWeather,
            );
            if (destinationWeatherCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === destinationWeatherCriterion.id,
              );
              if (criterion != null) {
                return criterion.values.find((x) => x.code !== 'LOCATION').value.split(',');
              }
            }
          }
          return null;
        },
        set(val) {
          const destinationWeatherTargetingCriteria = this.getWeatherTargetingCriteria(
            val,
            this.destinationWeatherTargetingTemperatureCode,
            this.destinationWeatherTargetingTemperatureValue,
            false,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) =>
              x.type !== TargetingCriteriaType.DestinationWeather &&
              x.type !== TargetingCriteriaType.DestinationTemperature,
          );
          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(destinationWeatherTargetingCriteria == null ? [] : destinationWeatherTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      destinationWeatherTargetingTemperatureCode: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const temperatureCriterion = this.targetingCriteria.find(
              (x) => x.type === TargetingCriteriaType.DestinationTemperature,
            );
            if (temperatureCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === temperatureCriterion.id,
              );
              if (criterion != null) {
                return criterion.values.find((x) => x.code !== 'LOCATION').code;
              }
            }
          }
          return null;
        },
        set(val) {
          const destinationWeatherTargetingCriteria = this.getWeatherTargetingCriteria(
            this.destinationWeatherTargetingConditions,
            val,
            val == null ? null : this.destinationWeatherTargetingTemperatureValue,
            false,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) =>
              x.type !== TargetingCriteriaType.DestinationWeather &&
              x.type !== TargetingCriteriaType.DestinationTemperature,
          );

          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(destinationWeatherTargetingCriteria == null ? [] : destinationWeatherTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      destinationWeatherTargetingTemperatureValue: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const temperatureCriterion = this.targetingCriteria.find(
              (x) => x.type === TargetingCriteriaType.DestinationTemperature,
            );
            if (temperatureCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === temperatureCriterion.id,
              );
              if (criterion != null) {
                return parseFloat(criterion.values.find((x) => x.code !== 'LOCATION').value);
              }
            }
          }
          return null;
        },
        set(val) {
          const destinationWeatherTargetingCriteria = this.getWeatherTargetingCriteria(
            this.destinationWeatherTargetingConditions,
            val == null ? null : this.destinationWeatherTargetingTemperatureCode,
            val,
            false,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) =>
              x.type !== TargetingCriteriaType.DestinationWeather &&
              x.type !== TargetingCriteriaType.DestinationTemperature,
          );

          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(destinationWeatherTargetingCriteria == null ? [] : destinationWeatherTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      destinationSnowAccumulationCode: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const snowCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.DestinationSnow);
            if (snowCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === snowCriterion.id,
              );
              if (criterion != null) {
                return criterion.values.find((x) => x.code !== 'LOCATION').code;
              }
            }
          }
          return null;
        },
        set(val) {
          const snowTargetingCriteria = this.getDestinationSnowTargetingCriteria(
            val,
            val == null ? null : this.destinationSnowAccumulationValue,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) => x.type !== TargetingCriteriaType.DestinationSnow,
          );

          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(snowTargetingCriteria == null ? [] : snowTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      destinationSnowAccumulationValue: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const snowCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.DestinationSnow);
            if (snowCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === snowCriterion.id,
              );
              if (criterion != null) {
                return parseFloat(criterion.values.find((x) => x.code !== 'LOCATION').value);
              }
            }
          }
          return null;
        },
        set(val) {
          const snowTargetingCriteria = this.getDestinationSnowTargetingCriteria(
            val == null ? null : this.destinationSnowAccumulationCode,
            val,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) => x.type !== TargetingCriteriaType.DestinationSnow,
          );

          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(snowTargetingCriteria == null ? [] : snowTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      destinationRainAccumulationCode: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const rainCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.DestinationRain);
            if (rainCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === rainCriterion.id,
              );
              if (criterion != null) {
                return criterion.values.find((x) => x.code !== 'LOCATION').code;
              }
            }
          }
          return null;
        },
        set(val) {
          const rainTargetingCriteria = this.getDestinationRainTargetingCriteria(
            val,
            val == null ? null : this.destinationRainAccumulationValue,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) => x.type !== TargetingCriteriaType.DestinationRain,
          );

          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(rainTargetingCriteria == null ? [] : rainTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
      destinationRainAccumulationValue: {
        get() {
          if (this.targetingCriteria != null && this.targetingCriteria.length > 0) {
            const rainCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.DestinationRain);
            if (rainCriterion != null && this.creative.creativeTargetingCriteria != null) {
              const criterion = this.creative.creativeTargetingCriteria.find(
                (x) => x.targetingCriterionID === rainCriterion.id,
              );
              if (criterion != null) {
                return parseFloat(criterion.values.find((x) => x.code !== 'LOCATION').value);
              }
            }
          }
          return null;
        },
        set(val) {
          const rainTargetingCriteria = this.getDestinationRainTargetingCriteria(
            val == null ? null : this.destinationRainAccumulationCode,
            val,
          );
          const otherTargetingCriterion = this.targetingCriteria.filter(
            (x) => x.type !== TargetingCriteriaType.DestinationRain,
          );

          this.updateCreativeTargetingCriteria({
            creative: this.creative,
            value: [
              ...(rainTargetingCriteria == null ? [] : rainTargetingCriteria),
              ...(otherTargetingCriterion.length > 0
                ? this.creative.creativeTargetingCriteria.filter((x) =>
                    otherTargetingCriterion.some((t) => t.id === x.targetingCriterionID),
                  )
                : []),
            ],
          });
        },
      },
    },
    methods: {
      capitalize,
      ...mapActions(['updateCreativeTargetingCriteria']),
      getDestinationSnowTargetingCriteria(code, value) {
        const criteria = [];
        const snowCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.DestinationSnow);
        if (code != null || value != null) {
          const criterion = new CreativeTargetingCriterion(this.creative.id, snowCriterion.id, [
            {
              code: code != null ? code : 'SNOW_CM_LAST_12H_ABOVE',
              value: value != null ? `${value}` : '1',
            },
          ]);
          this.addLocationToCriterion(criterion);
          criteria.push(criterion);
        }
        if (criteria.length > 0) {
          return criteria;
        }
        return null;
      },
      getDestinationRainTargetingCriteria(code, value) {
        const criteria = [];
        const rainCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.DestinationRain);
        if (code != null || value != null) {
          const criterion = new CreativeTargetingCriterion(this.creative.id, rainCriterion.id, [
            {
              code: code != null ? code : 'RAIN_MM_LAST_12H_ABOVE',
              value: value != null ? `${value}` : '1',
            },
          ]);
          this.addLocationToCriterion(criterion);
          criteria.push(criterion);
        }

        if (criteria.length > 0) {
          return criteria;
        }
        return null;
      },
      addLocationToCriterion(criterion) {
        if (this.destinationLatitude != null && this.destinationLongitude != null) {
          criterion.values.push({
            code: 'LOCATION',
            value: `${this.destinationLatitude},${this.destinationLongitude}`,
          });
        }
      },
      getWeatherTargetingCriteria(
        conditions,
        weatherTargetingTemperatureCode,
        weatherTargetingTemperatureValue,
        isLocal = true,
      ) {
        const weatherCode = isLocal ? TargetingCriteriaType.Weather : TargetingCriteriaType.DestinationWeather;
        const criteria = [];
        if (conditions != null) {
          const weatherCriterion = this.targetingCriteria.find((x) => x.type === weatherCode);

          const criterion = new CreativeTargetingCriterion(this.creative.id, weatherCriterion.id, [
            {
              code: TargetingCriteriaType.Weather,
              value: conditions.join(','),
            },
          ]);
          this.addLocationToCriterion(criterion);
          criteria.push(criterion);
        }
        if (weatherTargetingTemperatureCode != null || weatherTargetingTemperatureValue != null) {
          const temperatureCode = isLocal
            ? TargetingCriteriaType.Temperature
            : TargetingCriteriaType.DestinationTemperature;
          const temperatureCriterion = this.targetingCriteria.find((x) => x.type === temperatureCode);

          const criterion = new CreativeTargetingCriterion(this.creative.id, temperatureCriterion.id, [
            {
              code:
                weatherTargetingTemperatureCode != null
                  ? weatherTargetingTemperatureCode
                  : TargetingCriteriaType.DailyMaxAbove,
              value: weatherTargetingTemperatureValue != null ? `${weatherTargetingTemperatureValue}` : '20',
            },
          ]);

          this.addLocationToCriterion(criterion);
          criteria.push(criterion);
        }
        if (criteria.length > 0) {
          return criteria;
        }
        return null;
      },
      clear() {
        this.clearLocalTargeting();
        this.clearDestinationTargeting();
        this.$emit('cleared');
      },
      clearLocalTargeting() {
        this.clearUVTargeting();
        const destinationCriteriaIDs = this.targetingCriteria
          .filter((x) => x.type === TargetingCriteriaType.Weather || x.type === TargetingCriteriaType.Temperature)
          .map((x) => x.id);
        const newCreativeCriteria = this.creative.creativeTargetingCriteria.filter(
          (x) => !destinationCriteriaIDs.includes(x.targetingCriterionID),
        );
        this.updateCreativeTargetingCriteria({
          creative: this.creative,
          value: newCreativeCriteria,
        });
      },
      clearDestinationTargeting() {
        const destinationCriteriaIDs = this.targetingCriteria
          .filter(
            (x) =>
              x.type === TargetingCriteriaType.DestinationWeather ||
              x.type === TargetingCriteriaType.DestinationTemperature ||
              x.type === TargetingCriteriaType.DestinationSnow ||
              x.type === TargetingCriteriaType.DestinationRain,
          )
          .map((x) => x.id);
        const newCreativeCriteria = this.creative.creativeTargetingCriteria.filter(
          (x) => !destinationCriteriaIDs.includes(x.targetingCriterionID),
        );
        this.updateCreativeTargetingCriteria({
          creative: this.creative,
          value: newCreativeCriteria,
        });
      },
      getUVTargetingCriteria(conditions) {
        const criteria = [];
        if (conditions != null && conditions.length !== 0) {
          const uvCriterion = this.targetingCriteria.find((x) => x.type === TargetingCriteriaType.UV);

          criteria.push(
            new CreativeTargetingCriterion(this.creative.id, uvCriterion.id, [
              {
                code: TargetingCriteriaType.UvCategory,
                value: conditions.join(','),
              },
            ]),
          );
        }
        if (criteria.length > 0) {
          return criteria;
        }
        return null;
      },
      clearUVTargeting() {
        this.updateCreativeTargetingCriteria({ creative: this.creative, value: [] });
      },
    },
    async beforeMount() {
      await waitForPendingQueries([this.targetingCriteriaQuery]);
    },
    mounted() {
      this.isMounted = true;
    },
    unmounted() {
      this.isMounted = false;
    },
  };
</script>
